import { useQuery } from "@tanstack/react-query";
import { getQueryJobFilters, getQueryJobs } from "../../helpers/apis";
import styles from "./styles.module.scss";
import { DataTable } from "../../components/tables";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { parseSortDirection } from "@/components/tables/types";
import { RelativeComponentLoader } from "../../components/loader";
import { JobCostAndExecutionTimeGraph, JobExecutions, JobSelectedHeader } from "./JobExecutions";
import { Paginate } from "../../components/paginate";
import { GET_JOBS, GET_JOB_FILTERS } from "./constants";
import { FilterTagWrapper } from "../../components/tags";
import { useNavigate, useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { Typography } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import usePaginate from "@/components/paginate/usePaginate";
import { DownloadTable } from "@/components/downloadTable";
import { downloadAllQueryJobsCSV } from "@/helpers/apis";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { QueryJobItem, QueryJobsFilters, JobFilters } from "@/pages/query/types";
import { TableHeaderType, FilterType, SortDirection } from "@/components/tables/types";

const SORT_KEY_MAP: Record<string, keyof typeof INITIAL_SORT_ORDER> = {
  job_name: "job_name",
  avg_total_execution_time: "avg_total_execution_time",
  p90_total_execution_time: "p90_total_execution_time",
  max_total_execution_time: "max_total_execution_time",
  job_frequency: "job_frequency",
  annualized_cost: "annualized_cost",
  avg_local_spill: "avg_local_spill",
};

const INITIAL_SORT_ORDER = {
  job_name: "" as SortDirection | "",
  number_of_runs: "" as SortDirection | "",
  annualized_cost: "desc" as SortDirection | "",
  max_total_execution_time: "" as SortDirection | "",
  median_total_execution_time: "" as SortDirection | "",
  avg_total_execution_time: "" as SortDirection | "",
  p90_total_execution_time: "" as SortDirection | "",
  avg_local_spill: "" as SortDirection | "",
  job_frequency: "" as SortDirection | "",
};

interface JobListProps {
  setSelectedJob: (job: QueryJobItem | null) => void;
  jobFilters: JobFilters;
  autoTune?: boolean;
}

const JobList = ({ setSelectedJob, jobFilters, autoTune = false }: JobListProps) => {
  const { currency, currencySymbol } = useAppState();
  const navigate = useNavigate();
  const { jobID } = useParams();
  const [searchParams] = useSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const sortAttribute = searchParams.get("sort_key") || "annualized_cost";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");
  const annualizedCostFilter = jobFilters?.costFilter || 0;
  const warehouseFilter = Array.isArray(jobFilters?.warehouseFilter)
    ? jobFilters.warehouseFilter
    : [];
  const environmentFilter = (jobFilters?.environmentFilter as string) || "";

  const { data: filters } = useQuery({
    queryKey: [GET_JOB_FILTERS, autoTune],
    queryFn: () => getQueryJobFilters({ auto_tune: autoTune }),
  });

  const getFilterParams = (): QueryJobsFilters => {
    const params: QueryJobsFilters = { page: page + 1, size: pageSize };
    if (sortAttribute && sortAttribute in SORT_KEY_MAP) {
      params.sort_key = sortAttribute;
      params.sort_order = sortOrder;
    }
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (autoTune) params.auto_tune = true;
    if (environmentFilter) params.environments = [environmentFilter];
    if (annualizedCostFilter) params.annualized_cost = String(annualizedCostFilter);
    if (jobID) params.job_id = jobID;
    return params;
  };

  const { data, isFetching: isLoading } = useQuery({
    queryKey: [
      GET_JOBS,
      page,
      pageSize,
      ...warehouseFilter,
      ...environmentFilter,
      sortOrder,
      sortAttribute,
      annualizedCostFilter,
      jobID,
      autoTune,
    ],
    queryFn: () => getQueryJobs(getFilterParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (data && jobID) {
      const job = data["items"].find((item: QueryJobItem) => item.job_id === jobID);
      if (job) {
        setSelectedJob(job);
      }
    }
  }, [data, jobID, setSelectedJob]);

  const handleItemClick = (item: QueryJobItem) => {
    setPage(0);
    if (autoTune) {
      navigate(`/query/autotune_jobs/${item.job_id}`);
    } else {
      navigate(`/query/jobs/${item.job_id}`);
    }
  };

  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadAllQueryJobsCSV(params);
  };

  if (isLoading) return <RelativeComponentLoader />;

  const listingTableHeaders: TableHeaderType<QueryJobItem>[] = [
    {
      id: 1,
      label: "Job name",
      uid: "job_name",
      width: "10%",
      render: (item: QueryJobItem) => (
        <div className="text-primary al-break-all">{item.job_name}</div>
      ),
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 2,
      label: "Average run time",
      uid: "avg_total_execution_time",
      render: (item: QueryJobItem) => (
        <div className={"al-break-words"}>{item?.avg_total_execution_time}</div>
      ),
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 3,
      label: "90th percentile",
      uid: "p90_total_execution_time",
      render: (item: QueryJobItem) => (
        <div className={"al-break-words"}>{item?.p90_total_execution_time}</div>
      ),
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 4,
      label: "Max run time",
      uid: "max_total_execution_time",
      render: (item: QueryJobItem) => (
        <div className={"al-break-words"}>{item?.max_total_execution_time}</div>
      ),
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 5,
      label: "Job Frequency",
      uid: "job_frequency",
      render: (item: QueryJobItem) => <div className={"al-break-words"}>{item?.job_frequency}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 6,
      label: "Annualized cost",
      uid: "annualized_cost",
      render: (item: QueryJobItem) => (
        <div className={"al-break-words"}>
          {formatNumber(item?.annualized_cost ?? "", { currency })}
        </div>
      ),
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      filter: {
        filterType: FilterType.TEXT,
        searchKey: "cost",
        value: String(annualizedCostFilter) || undefined,
        placeHolder: `Specify cost in ${currencySymbol}`,
        label: "Annualized Cost",
      },
    },
    {
      id: 7,
      label: "Warehouses",
      uid: "warehouses",
      render: (item: QueryJobItem) => (
        <div className={"al-break-words"}>{item?.warehouses?.join(", ")}</div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        searchKey: "warehouses",
        value: warehouseFilter,
        options: filters?.warehouses
          ? (filters.warehouses.filter(Boolean) as string[]).map((warehouse: string) => ({
              label: warehouse,
              value: warehouse,
            }))
          : [],
      },
    },
  ];

  if (autoTune) {
    listingTableHeaders.splice(1, 0, {
      id: 8,
      label: "Environment",
      uid: "environment",
      render: (item: QueryJobItem) =>
        (autoTune && <div className={"al-break-words"}>{item?.environment}</div>) || null,
      filter: {
        filterType: FilterType.DROPDOWN,
        value: environmentFilter ? [environmentFilter] : [],
        searchKey: "environments",
        options: (filters?.environments ?? []).filter(Boolean).map(env => ({
          label: env as string,
          value: env as string,
        })),
      },
    });

    listingTableHeaders.splice(8, 0, {
      id: 9,
      label: "Avg. Local Spill",
      uid: "avg_local_spill",
      render: (item: QueryJobItem) =>
        (autoTune && <div className={"al-break-words"}>{item?.avg_local_spill}</div>) || null,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    });
  }

  return (
    <div className={styles.jobs}>
      <Stack className="al-mt-2 al-mb-2" align="center">
        <Typography variant="page-heading">Query Jobs</Typography>
        <FilterTagWrapper
          applicableFor={autoTune ? FilterApplicableFor.AUTOTUNE_JOBS : FilterApplicableFor.JOBS}
          filters={{
            annualizedCostFilter: {
              label: "Annualized Cost",
              filterStr: String(annualizedCostFilter),
              searchKey: "cost",
              onclose: () => {},
            },
            warehouseFilter: {
              label: "Warehouse",
              filterStr: warehouseFilter,
              searchKey: "warehouses",
              onclose: () => {},
            },
            environmentFilter: {
              label: "Environment",
              filterStr: environmentFilter,
              searchKey: "environments",
              onclose: () => {},
            },
          }}
        />
        <div className="spacer" />
        <DownloadTable handleDownload={handleDownloadClick} />
        <SavedFilters
          applicableFor={autoTune ? FilterApplicableFor.AUTOTUNE_JOBS : FilterApplicableFor.JOBS}
        />
        <div id="columnpicker-container" />
      </Stack>
      <DataTable
        header={listingTableHeaders}
        onRowClick={handleItemClick}
        idKey={(item: QueryJobItem) => item.job_id}
        data={{ items: data?.items ?? [] }}
        resetPage={() => {}}
        applicableFor={autoTune ? FilterApplicableFor.AUTOTUNE_JOBS : FilterApplicableFor.JOBS}
        columnPickerPortal="columnpicker-container"
      />
      <Paginate
        itemCount={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        numPages={data?.pages ?? 0}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </div>
  );
};

const Jobs = () => {
  const [selectedJob, setSelectedJob] = useState<QueryJobItem | null>(null);
  const { searchParams } = useFiltersBySearchParams();

  useBreadcrumb(selectedJob?.job_name ? selectedJob.job_name : "Jobs", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Jobs", pathname: "/query/jobs" },
    ],
    truncateTo: "/query",
  });

  const jobFilters: JobFilters = {
    costFilter: searchParams.getAll("cost")[0] || 0,
    warehouseFilter: searchParams.getAll("warehouses") || [],
    userFilter: searchParams.getAll("users") || [],
    warehouseSizeFilter: searchParams.getAll("warehouseSize") || [],
  };

  return (
    <>
      {!selectedJob ? (
        <JobList setSelectedJob={setSelectedJob} jobFilters={jobFilters} />
      ) : (
        <Stack direction="column">
          <JobSelectedHeader
            initialJobName={selectedJob?.job_name ?? ""}
            jobData={selectedJob}
            close={() => setSelectedJob(null)}
          />
          <JobCostAndExecutionTimeGraph jobId={selectedJob?.job_id} />
          <JobExecutions selectedJob={selectedJob} jobFilters={jobFilters} />
        </Stack>
      )}
    </>
  );
};

const AutoTuneJobs = () => {
  const [selectedJob, setSelectedJob] = useState<QueryJobItem | null>(null);
  const { searchParams } = useFiltersBySearchParams();

  useBreadcrumb(selectedJob?.job_name ? selectedJob.job_name : "AutoTune Jobs", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "AutoTune Jobs", pathname: "/query/autotune_jobs" },
    ],
    truncateTo: "/query",
  });

  const jobFilters: JobFilters = {
    costFilter: searchParams.getAll("cost")[0] || 0,
    warehouseFilter: searchParams.getAll("warehouses") || [],
    warehouseSizeFilter: searchParams.getAll("warehouseSize") || [],
    userFilter: searchParams.getAll("users") || [],
    environmentFilter: searchParams.getAll("environments")[0] || "",
  };

  return (
    <>
      {!selectedJob ? (
        <JobList autoTune={true} setSelectedJob={setSelectedJob} jobFilters={jobFilters} />
      ) : (
        <Stack direction="column">
          <JobSelectedHeader
            environment={selectedJob?.environment ?? undefined}
            initialJobName={selectedJob?.job_name ?? ""}
            jobData={selectedJob}
            close={() => setSelectedJob(null)}
          />
          <JobCostAndExecutionTimeGraph jobId={selectedJob?.job_id} />
          <JobExecutions selectedJob={selectedJob} jobFilters={jobFilters} autoTune={true} />
        </Stack>
      )}
    </>
  );
};

export { Jobs, AutoTuneJobs };
